/**
 * Metafield helper functions for campaign management
 * Uses App Bridge graphqlCall for Shopify Admin API
 */

import { graphqlCall } from './Common';

// Metafield constants
export const METAFIELD_NAMESPACE = 'custom';
export const METAFIELD_KEY = 'campaign_data';
export const METAFIELD_TYPE = 'json';

/**
 * Get existing metafield for a product
 * @param {string} productId - Product GID
 * @returns {Promise<Object|null>} Metafield data or null
 */
export async function getProductMetafield(productId, returnId = false) {
    if (!productId) return null;

    try {
        const query = {
            query: `
                query GetProductMetafield($id: ID!) {
                    product(id: $id) {
                        id
                        metafield(namespace: "${METAFIELD_NAMESPACE}", key: "${METAFIELD_KEY}") {
                            id
                            namespace
                            key
                            value
                            type
                        }
                    }
                }
            `,
            variables: {
                id: productId
            }
        };

        const response = await graphqlCall(query);
        if (returnId) {
            return response.data?.product?.metafield?.id || null;
        }
        return response.data?.product?.metafield || null;
    } catch (error) {
        console.warn('Could not load existing metafield:', error);
        return null;
    }
}

/**
 * Create metafield on a product
 * @deprecated Use createOrUpdateProductMetafield instead - it's more efficient and works for both create and update
 * @param {string} productId - Product GID
 * @param {Object} campaignData - Campaign data to store
 * @returns {Promise<Object|null>} Created metafield or null
 */
export async function createProductMetafield(productId, campaignData, metafieldKey = METAFIELD_KEY) {
    // Use unified function for better performance
    return createOrUpdateProductMetafield(productId, campaignData, metafieldKey);
}

/**
 * Create or update metafield on a product (unified function)
 * Uses metafieldsSet which works for both create and update without needing to fetch first
 * This is more efficient than separate create/update functions
 * @param {string} productId - Product GID
 * @param {Object} campaignData - Campaign data to store
 * @returns {Promise<Object|null>} Created/updated metafield or null
 */
export async function createOrUpdateProductMetafield(productId, campaignData, metafieldKey = METAFIELD_KEY) {
    if (!productId || !campaignData) {
        throw new Error('Product ID and campaign data are required');
    }

    const query = {
        query: `
            mutation CreateOrUpdateProductMetafield($metafields: [MetafieldsSetInput!]!) {
                metafieldsSet(metafields: $metafields) {
                    metafields {
                        id
                        namespace
                        key
                        value
                        type
                    }
                    userErrors {
                        field
                        message
                    }
                }
            }
        `,
        variables: {
            metafields: [
                {
                    ownerId: productId,
                    namespace: METAFIELD_NAMESPACE,
                    key: metafieldKey,
                    value: JSON.stringify(campaignData),
                    type: METAFIELD_TYPE
                }
            ]
        }
    };

    const response = await graphqlCall(query);
    
    if (response.errors) {
        throw new Error(response.errors[0]?.message || 'Failed to create/update metafield');
    }
    
    if (response.data?.metafieldsSet?.userErrors?.length > 0) {
        throw new Error(response.data.metafieldsSet.userErrors[0].message || 'Failed to create/update metafield');
    }

    return response.data?.metafieldsSet?.metafields?.[0] || null;
}

/**
 * Update existing metafield on a product
 * @deprecated Use createOrUpdateProductMetafield instead - it's more efficient
 * @param {string} productId - Product GID
 * @param {Object} campaignData - Campaign data to store
 * @returns {Promise<Object|null>} Updated metafield or null
 */
export async function updateProductMetafield(productId, campaignData) {
    // Use unified function for better performance
    return createOrUpdateProductMetafield(productId, campaignData);
}

/**
 * Delete metafield from a product (with fallback)
 * @param {string} metafieldId - Metafield GID (optional if productId, namespace, and key are provided)
 * @param {string} productId - Product GID (required if metafieldId not provided)
 * @param {string} namespace - Namespace (required if metafieldId not provided)
 * @param {string} key - Key (required if metafieldId not provided)
 * @returns {Promise<boolean>} True if deleted successfully
 */
export async function deleteProductMetafield(metafieldId, productId = null, namespace = null, key = null) {
    let metafieldData = null;

    try {
        // If we have productId, namespace, and key, use them directly (no need to fetch)
        if (productId && namespace && key) {
            metafieldData = {
                ownerId: productId,
                namespace: namespace,
                key: key
            };
        } else if (metafieldId) {
            // Only fetch if we don't have productId, namespace, and key
            // MetafieldIdentifierInput requires ownerId, namespace, and key (not id)
            const getMetafieldQuery = {
                query: `
                    query GetMetafield($id: ID!) {
                        metafield(id: $id) {
                            id
                            namespace
                            key
                            ownerId
                        }
                    }
                `,
                variables: {
                    id: metafieldId
                }
            };

            const metafieldResponse = await graphqlCall(getMetafieldQuery);
            
            // Check if we got valid metafield data
            if (!metafieldResponse.errors && metafieldResponse.data?.metafield) {
                const metafield = metafieldResponse.data.metafield;
                
                if (metafield.namespace && metafield.key && metafield.ownerId) {
                    metafieldData = {
                        ownerId: metafield.ownerId,
                        namespace: metafield.namespace,
                        key: metafield.key
                    };
                }
            }

            // If fetch failed but we have productId and known namespace/key, use those
            if (!metafieldData && productId && namespace && key) {
                metafieldData = {
                    ownerId: productId,
                    namespace: namespace,
                    key: key
                };
            }
        }

        // If we still don't have metafield data, try fallback or throw error
        if (!metafieldData) {
            // Try fallback: Clear metafield with empty JSON if productId and namespace/key provided
            if (productId && namespace && key) {
                return await clearProductMetafield(productId, namespace, key);
            }
            throw new Error('Cannot delete metafield - missing required data (need either metafieldId or productId+namespace+key)');
        }

        // Use metafieldsDelete mutation (new API version 2025-01+)
        const deleteQuery = {
            query: `
                mutation MetafieldsDelete($metafields: [MetafieldIdentifierInput!]!) {
                    metafieldsDelete(metafields: $metafields) {
                        deletedMetafields {
                            key
                            namespace
                            ownerId
                        }
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                metafields: [metafieldData]
            }
        };

        const deleteResponse = await graphqlCall(deleteQuery);
        
        // Check if mutation exists
        if (deleteResponse.errors) {
            const errorMessage = deleteResponse.errors[0]?.message || '';
            if (errorMessage.includes("doesn't exist") || errorMessage.includes("Unknown field")) {
                // Fallback: Clear metafield with empty JSON if productId and namespace/key provided
                if (productId && namespace && key) {
                    return await clearProductMetafield(productId, namespace, key);
                }
                throw new Error('metafieldsDelete not available and no productId+namespace+key for fallback');
            }
            throw new Error(errorMessage || 'Failed to delete metafield');
        }

        if (deleteResponse.data?.metafieldsDelete?.userErrors?.length > 0) {
            throw new Error(deleteResponse.data.metafieldsDelete.userErrors[0].message || 'Failed to delete metafield');
        }

        // Check if metafield was deleted successfully
        return !!deleteResponse.data?.metafieldsDelete?.deletedMetafields?.length;
    } catch (error) {
        console.warn('Could not delete metafield:', error);
        // Try fallback if productId and namespace/key provided
        if (productId && namespace && key) {
            return await clearProductMetafield(productId, namespace, key);
        }
        return false;
    }
}

/**
 * Clear metafield by setting empty JSON (fallback method)
 * @param {string} productId - Product GID
 * @param {string} namespace - Namespace (defaults to METAFIELD_NAMESPACE)
 * @param {string} key - Key (defaults to METAFIELD_KEY)
 * @returns {Promise<boolean>} True if cleared successfully
 */
async function clearProductMetafield(productId, namespace = METAFIELD_NAMESPACE, key = METAFIELD_KEY) {
    try {
        const query = {
            query: `
                mutation ClearMetafield($metafields: [MetafieldsSetInput!]!) {
                    metafieldsSet(metafields: $metafields) {
                        metafields {
                            id
                        }
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                metafields: [
                    {
                        ownerId: productId,
                        namespace: namespace,
                        key: key,
                        value: '{}',
                        type: METAFIELD_TYPE
                    }
                ]
            }
        };

        const response = await graphqlCall(query);
        
        if (response.errors || response.data?.metafieldsSet?.userErrors?.length > 0) {
            throw new Error('Failed to clear metafield using alternative method');
        }

        return true;
    } catch (error) {
        console.warn('Could not clear metafield:', error);
        return false;
    }
}

// Shop Metafield constants for settings
export const SHOP_METAFIELD_NAMESPACE = 'custom';
export const SHOP_METAFIELD_KEY = 'campaign_settings';
export const SHOP_METAFIELD_TYPE = 'json';

// Custom CSS and Script metafield constants
export const CUSTOM_CSS_METAFIELD_KEY = 'custom_css';
export const CUSTOM_SCRIPT_METAFIELD_KEY = 'custom_script';
export const CUSTOM_CSS_METAFIELD_TYPE = 'multi_line_text_field';
export const CUSTOM_SCRIPT_METAFIELD_TYPE = 'multi_line_text_field';

/**
 * Get Shop GID from shopify_id
 * @param {number|string} shopifyId - Shopify ID from install API response
 * @returns {string} Shop GID in format gid://shopify/Shop/{shopifyId}
 */
function getShopGid(shopifyId) {
    if (!shopifyId) {
        throw new Error('Shopify ID is required');
    }
    // Convert to number if it's a string
    const id = typeof shopifyId === 'string' ? parseInt(shopifyId) : shopifyId;
    if (isNaN(id)) {
        throw new Error(`Invalid shopify_id: ${shopifyId}`);
    }
    return `gid://shopify/Shop/${id}`;
}

/**
 * Get existing shop metafield for settings
 * Shop metafields are stored on Shop
 * @param {number|string} shopifyId - Shopify ID from install API response (used for validation, not query)
 * @returns {Promise<Object|null>} Metafield data or null
 */
export async function getAppMetafield(shopifyId) {
    if (!shopifyId) {
        console.warn('Shopify ID is required to get shop metafield');
        return null;
    }

    try {
        // Shop query doesn't require ID parameter - there's only one shop per context
        const query = {
            query: `
                query GetShopMetafield {
                    shop {
                        id
                        metafield(namespace: "${SHOP_METAFIELD_NAMESPACE}", key: "${SHOP_METAFIELD_KEY}") {
                            id
                            namespace
                            key
                            value
                            type
                        }
                    }
                }
            `
        };

        const response = await graphqlCall(query);
        
        if (response.errors) {
            console.warn('GraphQL errors getting shop metafield:', response.errors);
            return null;
        }
        
        return response.data?.shop?.metafield || null;
    } catch (error) {
        console.warn('Could not load shop metafield:', error);
        return null;
    }
}

/**
 * Create or update shop metafield for settings
 * @param {Object} settingsData - Settings data to store
 * @param {number|string} shopifyId - Shopify ID from install API response
 * @returns {Promise<Object|null>} Created/updated metafield or null
 */
export async function createOrUpdateAppMetafield(settingsData, shopifyId) {
    if (!settingsData) {
        throw new Error('Settings data is required');
    }

    if (!shopifyId) {
        throw new Error('Shopify ID is required. Please ensure you are accessing the app through Shopify Admin.');
    }

    try {
        const shopGid = getShopGid(shopifyId);
        const metafieldValue = JSON.stringify(settingsData);

        // metafieldsSet works for both create and update - just provide ownerId, namespace, key, value, and type
        // If a metafield with that combination exists, it updates; otherwise, it creates
        // No need to fetch first - metafieldsSet handles both cases
        
        const setQuery = {
            query: `
                mutation SetShopMetafield($metafields: [MetafieldsSetInput!]!) {
                    metafieldsSet(metafields: $metafields) {
                        metafields {
                            id
                            namespace
                            key
                            value
                            type
                        }
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                metafields: [
                    {
                        ownerId: shopGid,
                        namespace: SHOP_METAFIELD_NAMESPACE,
                        key: SHOP_METAFIELD_KEY,
                        value: metafieldValue,
                        type: SHOP_METAFIELD_TYPE
                    }
                ]
            }
        };

        const setResponse = await graphqlCall(setQuery);

        if (setResponse.errors) {
            const errorMsg = setResponse.errors[0]?.message || 'Failed to set shop metafield';
            throw new Error(errorMsg);
        }
        
        if (setResponse.data?.metafieldsSet?.userErrors?.length > 0) {
            const errorMsg = setResponse.data.metafieldsSet.userErrors[0].message || 'Failed to set shop metafield';
            console.error('Set metafield userErrors:', setResponse.data.metafieldsSet.userErrors);
            throw new Error(errorMsg);
        }

        return setResponse.data?.metafieldsSet?.metafields?.[0] || null;
    } catch (error) {
        console.error('Error creating/updating shop metafield:', error);
        throw error;
    }
}

/**
 * Get custom CSS metafield from shop
 * @param {number|string} shopifyId - Shopify ID from install API response
 * @returns {Promise<string|null>} CSS content or null
 */
export async function getCustomCssMetafield(shopifyId) {
    if (!shopifyId) {
        console.warn('Shopify ID is required to get custom CSS metafield');
        return null;
    }

    try {
        const query = {
            query: `
                query GetCustomCssMetafield {
                    shop {
                        id
                        metafield(namespace: "${SHOP_METAFIELD_NAMESPACE}", key: "${CUSTOM_CSS_METAFIELD_KEY}") {
                            id
                            namespace
                            key
                            value
                            type
                        }
                    }
                }
            `
        };

        const response = await graphqlCall(query);
        
        if (response.errors) {
            console.warn('GraphQL errors getting custom CSS metafield:', response.errors);
            return null;
        }
        
        return response.data?.shop?.metafield?.value || null;
    } catch (error) {
        console.warn('Could not load custom CSS metafield:', error);
        return null;
    }
}

/**
 * Get custom Script metafield from shop
 * @param {number|string} shopifyId - Shopify ID from install API response
 * @returns {Promise<string|null>} Script content or null
 */
export async function getCustomScriptMetafield(shopifyId) {
    if (!shopifyId) {
        console.warn('Shopify ID is required to get custom Script metafield');
        return null;
    }

    try {
        const query = {
            query: `
                query GetCustomScriptMetafield {
                    shop {
                        id
                        metafield(namespace: "${SHOP_METAFIELD_NAMESPACE}", key: "${CUSTOM_SCRIPT_METAFIELD_KEY}") {
                            id
                            namespace
                            key
                            value
                            type
                        }
                    }
                }
            `
        };

        const response = await graphqlCall(query);
        
        if (response.errors) {
            console.warn('GraphQL errors getting custom Script metafield:', response.errors);
            return null;
        }
        
        return response.data?.shop?.metafield?.value || null;
    } catch (error) {
        console.warn('Could not load custom Script metafield:', error);
        return null;
    }
}

/**
 * Create or update custom CSS metafield
 * @param {string} cssContent - CSS content to store
 * @param {number|string} shopifyId - Shopify ID from install API response
 * @returns {Promise<Object|null>} Created/updated metafield or null
 */
export async function createOrUpdateCustomCssMetafield(cssContent, shopifyId) {
    if (!shopifyId) {
        throw new Error('Shopify ID is required. Please ensure you are accessing the app through Shopify Admin.');
    }

    try {
        const shopGid = getShopGid(shopifyId);
        
        const setQuery = {
            query: `
                mutation SetCustomCssMetafield($metafields: [MetafieldsSetInput!]!) {
                    metafieldsSet(metafields: $metafields) {
                        metafields {
                            id
                            namespace
                            key
                            value
                            type
                        }
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                metafields: [
                    {
                        ownerId: shopGid,
                        namespace: SHOP_METAFIELD_NAMESPACE,
                        key: CUSTOM_CSS_METAFIELD_KEY,
                        value: cssContent || 'false',
                        type: CUSTOM_CSS_METAFIELD_TYPE
                    }
                ]
            }
        };

        const setResponse = await graphqlCall(setQuery);
        
        if (setResponse.errors) {
            const errorMsg = setResponse.errors[0]?.message || 'Failed to set custom CSS metafield';
            console.error('Set custom CSS metafield errors:', setResponse.errors);
            throw new Error(errorMsg);
        }
        
        if (setResponse.data?.metafieldsSet?.userErrors?.length > 0) {
            const errorMsg = setResponse.data.metafieldsSet.userErrors[0].message || 'Failed to set custom CSS metafield';
            console.error('Set custom CSS metafield userErrors:', setResponse.data.metafieldsSet.userErrors);
            throw new Error(errorMsg);
        }

        return setResponse.data?.metafieldsSet?.metafields?.[0] || null;
    } catch (error) {
        console.error('Error creating/updating custom CSS metafield:', error);
        throw error;
    }
}

/**
 * Create or update custom Script metafield
 * @param {string} scriptContent - Script content to store
 * @param {number|string} shopifyId - Shopify ID from install API response
 * @returns {Promise<Object|null>} Created/updated metafield or null
 */
export async function createOrUpdateCustomScriptMetafield(scriptContent, shopifyId) {
    if (!shopifyId) {
        throw new Error('Shopify ID is required. Please ensure you are accessing the app through Shopify Admin.');
    }

    try {
        const shopGid = getShopGid(shopifyId);
        
        const setQuery = {
            query: `
                mutation SetCustomScriptMetafield($metafields: [MetafieldsSetInput!]!) {
                    metafieldsSet(metafields: $metafields) {
                        metafields {
                            id
                            namespace
                            key
                            value
                            type
                        }
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                metafields: [
                    {
                        ownerId: shopGid,
                        namespace: SHOP_METAFIELD_NAMESPACE,
                        key: CUSTOM_SCRIPT_METAFIELD_KEY,
                        value: scriptContent || 'false',
                        type: CUSTOM_SCRIPT_METAFIELD_TYPE
                    }
                ]
            }
        };

        const setResponse = await graphqlCall(setQuery);
        
        if (setResponse.errors) {
            const errorMsg = setResponse.errors[0]?.message || 'Failed to set custom Script metafield';
            console.error('Set custom Script metafield errors:', setResponse.errors);
            throw new Error(errorMsg);
        }
        
        if (setResponse.data?.metafieldsSet?.userErrors?.length > 0) {
            const errorMsg = setResponse.data.metafieldsSet.userErrors[0].message || 'Failed to set custom Script metafield';
            console.error('Set custom Script metafield userErrors:', setResponse.data.metafieldsSet.userErrors);
            throw new Error(errorMsg);
        }

        return setResponse.data?.metafieldsSet?.metafields?.[0] || null;
    } catch (error) {
        console.error('Error creating/updating custom Script metafield:', error);
        throw error;
    }
}

/**
 * Batch create or update all shop settings metafields in a single GraphQL call
 * This is more efficient than calling each metafield function separately
 * @param {Object} settingsData - Settings data to store
 * @param {string} cssContent - Custom CSS content
 * @param {string} scriptContent - Custom Script content
 * @param {number|string} shopifyId - Shopify ID from install API response
 * @returns {Promise<Object>} Result with metafields array and any errors
 */
export async function batchCreateOrUpdateShopMetafields(settingsData, cssContent, scriptContent, shopifyId) {
    if (!shopifyId) {
        throw new Error('Shopify ID is required. Please ensure you are accessing the app through Shopify Admin.');
    }

    try {
        const shopGid = getShopGid(shopifyId);
        
        // Build metafields array for batch operation
        const metafields = [];
        
        // Add settings metafield
        if (settingsData !== null && settingsData !== undefined) {
            metafields.push({
                ownerId: shopGid,
                namespace: SHOP_METAFIELD_NAMESPACE,
                key: SHOP_METAFIELD_KEY,
                value: JSON.stringify(settingsData),
                type: SHOP_METAFIELD_TYPE
            });
        }
        
        // Add CSS metafield
        if (cssContent !== null && cssContent !== undefined) {
            metafields.push({
                ownerId: shopGid,
                namespace: SHOP_METAFIELD_NAMESPACE,
                key: CUSTOM_CSS_METAFIELD_KEY,
                value: cssContent || 'false',
                type: CUSTOM_CSS_METAFIELD_TYPE
            });
        }
        
        // Add Script metafield
        if (scriptContent !== null && scriptContent !== undefined) {
            metafields.push({
                ownerId: shopGid,
                namespace: SHOP_METAFIELD_NAMESPACE,
                key: CUSTOM_SCRIPT_METAFIELD_KEY,
                value: scriptContent || 'false',
                type: CUSTOM_SCRIPT_METAFIELD_TYPE
            });
        }
        
        if (metafields.length === 0) {
            throw new Error('At least one metafield must be provided');
        }
        
        const setQuery = {
            query: `
                mutation BatchSetShopMetafields($metafields: [MetafieldsSetInput!]!) {
                    metafieldsSet(metafields: $metafields) {
                        metafields {
                            id
                            namespace
                            key
                            value
                            type
                        }
                        userErrors {
                            field
                            message
                        }
                    }
                }
            `,
            variables: {
                metafields
            }
        };

        const setResponse = await graphqlCall(setQuery);
        
        if (setResponse.errors) {
            const errorMsg = setResponse.errors[0]?.message || 'Failed to batch set shop metafields';
            console.error('Batch set shop metafields errors:', setResponse.errors);
            throw new Error(errorMsg);
        }
        
        if (setResponse.data?.metafieldsSet?.userErrors?.length > 0) {
            const errorMsg = setResponse.data.metafieldsSet.userErrors[0].message || 'Failed to batch set shop metafields';
            console.error('Batch set shop metafields userErrors:', setResponse.data.metafieldsSet.userErrors);
            throw new Error(errorMsg);
        }

        return {
            metafields: setResponse.data?.metafieldsSet?.metafields || [],
            success: true
        };
    } catch (error) {
        console.error('Error batch creating/updating shop metafields:', error);
        throw error;
    }
}

/**
 * Batch get all shop settings metafields efficiently
 * Makes parallel requests to get all three metafields at once
 * @param {number|string} shopifyId - Shopify ID from install API response (used for validation, not query)
 * @returns {Promise<Object>} Object with settings, customCss, and customScript
 */
export async function batchGetShopMetafields(shopifyId) {
    if (!shopifyId) {
        console.warn('Shopify ID is required to get shop metafields');
        return { settings: null, customCss: null, customScript: null };
    }

    try {
        // Make all three queries in parallel for better performance
        const [settingsResult, cssResult, scriptResult] = await Promise.allSettled([
            getAppMetafield(shopifyId),
            getCustomCssMetafield(shopifyId),
            getCustomScriptMetafield(shopifyId)
        ]);

        return {
            settings: settingsResult.status === 'fulfilled' ? settingsResult.value : null,
            customCss: cssResult.status === 'fulfilled' ? cssResult.value : null,
            customScript: scriptResult.status === 'fulfilled' ? scriptResult.value : null
        };
    } catch (error) {
        console.warn('Could not load batch shop metafields:', error);
        return { settings: null, customCss: null, customScript: null };
    }
}

// Campaign Count Metafield Constants
export const CAMPAIGN_COUNT_NAMESPACE = 'custom';
export const CAMPAIGN_COUNT_KEY = 'Campign_count';
export const CAMPAIGN_COUNT_TYPE = 'json';

/**
 * Get existing campaign count metafield for a product
 * @param {string} productId - Product GID
 * @returns {Promise<Object|null>} Metafield data or null
 */
export async function getCampaignCountMetafield(productId) {
    if (!productId) return null;

    try {
        const query = {
            query: `
                query GetCampaignCountMetafield($id: ID!) {
                    product(id: $id) {
                        id
                        metafield(namespace: "${CAMPAIGN_COUNT_NAMESPACE}", key: "${CAMPAIGN_COUNT_KEY}") {
                            id
                            namespace
                            key
                            value
                            type
                        }
                    }
                }
            `,
            variables: {
                id: productId
            }
        };

        const response = await graphqlCall(query);
        return response.data?.product?.metafield || null;
    } catch (error) {
        console.warn('Could not load campaign count metafield:', error);
        return null;
    }
}

/**
 * Create or update campaign count metafield on a product
 * @param {string} productId - Product GID
 * @param {Object} campaignCountData - Campaign count data to store
 * @returns {Promise<Object|null>} Created/updated metafield or null
 */
export async function createOrUpdateCampaignCountMetafield(productId, campaignCountData) {
    if (!productId || !campaignCountData) {
        throw new Error('Product ID and campaign count data are required');
    }

    // No need to fetch first - metafieldsSet works for both create and update
    const query = {
        query: `
            mutation CreateOrUpdateCampaignCountMetafield($metafields: [MetafieldsSetInput!]!) {
                metafieldsSet(metafields: $metafields) {
                    metafields {
                        id
                        namespace
                        key
                        value
                        type
                    }
                    userErrors {
                        field
                        message
                    }
                }
            }
        `,
        variables: {
            metafields: [
                {
                    ownerId: productId,
                    namespace: CAMPAIGN_COUNT_NAMESPACE,
                    key: CAMPAIGN_COUNT_KEY,
                    value: JSON.stringify(campaignCountData),
                    type: CAMPAIGN_COUNT_TYPE
                }
            ]
        }
    };

    const response = await graphqlCall(query);
    
    if (response.errors) {
        throw new Error(response.errors[0]?.message || 'Failed to create/update campaign count metafield');
    }
    
    if (response.data?.metafieldsSet?.userErrors?.length > 0) {
        throw new Error(response.data.metafieldsSet.userErrors[0].message || 'Failed to create/update campaign count metafield');
    }

    return response.data?.metafieldsSet?.metafields?.[0] || null;
}

/**
 * Delete campaign count metafield from a product
 * @param {string} productId - Product GID
 * @returns {Promise<boolean>} True if deleted successfully
 */
export async function deleteCampaignCountMetafield(productId) {
    if (!productId) return false;

    try {
        // Directly delete using known namespace and key (no need to fetch first)
        return await deleteProductMetafield(
            null, // No metafieldId needed when we have productId, namespace, and key
            productId, 
            CAMPAIGN_COUNT_NAMESPACE, 
            CAMPAIGN_COUNT_KEY
        );
    } catch (error) {
        console.warn('Could not delete campaign count metafield:', error);
        return false;
    }
}

